import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import { DangerHero } from '@/components/DangerHero';
import { VideoTeaser } from '@/components/VideoTeaser';
import { LuckyDraw } from '@/components/LuckyDraw';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { playWhoosh, playHover, playPop, playClick } from '@/utils/sounds';
import { useAuth } from '@pooflabs/web';
import { Wallet, X as XIcon, Smartphone } from 'lucide-react';

// ─── WalletWarningBanner ──────────────────────────────────────────────────────

const WalletWarningBanner: React.FC = () => {
  const { user, loading, login } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when user connects
  useEffect(() => {
    if (user) setDismissed(false);
  }, [user]);

  if (loading || user || dismissed) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '7px 16px',
        background: 'linear-gradient(90deg, rgba(10,8,8,0.97) 0%, rgba(20,8,8,0.97) 40%, rgba(15,10,8,0.97) 100%)',
        borderBottom: '2px solid rgba(230,57,70,0.55)',
        boxShadow: '0 2px 16px rgba(230,57,70,0.18)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'banner-drop-in 0.4s cubic-bezier(0.22,1,0.36,1) both',
      }}
    >
      <style>{`
        @keyframes banner-drop-in {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes banner-pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
        .wallet-banner-dot {
          animation: banner-pulse-dot 1.5s ease-in-out infinite;
        }
        .wallet-banner-connect-btn:hover {
          background: rgba(230,57,70,0.25) !important;
          box-shadow: 0 0 14px rgba(230,57,70,0.5) !important;
        }
        .wallet-banner-dismiss:hover {
          opacity: 1 !important;
          color: #fff !important;
        }
      `}</style>

      {/* Pulsing danger dot */}
      <div
        className="wallet-banner-dot"
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: '#E63946',
          boxShadow: '0 0 8px rgba(230,57,70,0.8)',
          flexShrink: 0,
        }}
      />

      {/* Warning icon */}
      <Wallet
        size={14}
        style={{ color: '#E63946', flexShrink: 0 }}
        aria-hidden="true"
      />

      {/* Text */}
      <span
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)',
          letterSpacing: '0.1em',
          color: 'rgba(255,255,255,0.72)',
          whiteSpace: 'nowrap',
        }}
      >
        WALLET NOT CONNECTED
      </span>

      <span
        style={{
          fontSize: '0.7rem',
          color: 'rgba(255,255,255,0.3)',
          display: 'none',
        }}
        className="md:inline-block"
      >
        —
      </span>

      <span
        style={{
          fontSize: 'clamp(0.68rem, 1.8vw, 0.75rem)',
          color: 'rgba(255,255,255,0.45)',
          display: 'none',
        }}
        className="md:inline"
      >
        Connect your wallet to enter the lucky draw
      </span>

      {/* Connect button */}
      <button
        onClick={() => { playClick(); login(); }}
        className="wallet-banner-connect-btn"
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: 'clamp(0.72rem, 2vw, 0.82rem)',
          letterSpacing: '0.12em',
          color: '#E63946',
          background: 'rgba(230,57,70,0.1)',
          border: '1.5px solid rgba(230,57,70,0.55)',
          boxShadow: '0 0 8px rgba(230,57,70,0.2)',
          padding: '3px 14px',
          cursor: 'pointer',
          transition: 'all 0.18s ease',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        CONNECT
      </button>

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        className="wallet-banner-dismiss"
        aria-label="Dismiss wallet warning"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px',
          color: 'rgba(255,255,255,0.3)',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          marginLeft: '2px',
        }}
      >
        <XIcon size={13} />
      </button>
    </div>
  );
};

const DangerFooter: React.FC = () => {
  const footerRef = useScrollAnimation<HTMLElement>(0.05);
  return (
  <footer ref={footerRef} className="relative border-t scroll-fade-in" style={{ borderColor: 'rgba(124,252,0,0.1)' }}>
    <div
      className="absolute top-0 left-0 right-0 h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent 0%, #E63946 30%, #7CFC00 50%, #E63946 70%, transparent 100%)',
      }}
    />
    <div className="container py-4 md:py-10 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4">
      <div className="flex items-center gap-2 md:gap-3">
        <span
          className="text-base md:text-2xl font-bold uppercase"
          style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.08em', color: '#7CFC00' }}
        >
          Danger Dan!
        </span>
        <span className="text-white text-xs md:text-sm">© 2026</span>
      </div>
      <div className="flex items-center gap-2 md:gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/terms"
            className="text-base md:text-lg font-bold transition-all duration-200"
            style={{ color: '#ffffff' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#d0d0d0'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#ffffff'; }}
          >
            Terms of Service
          </Link>
          <span style={{ color: '#ffffff', fontSize: '1rem', fontWeight: 'bold' }}>•</span>
          <Link
            to="/privacy"
            className="text-base md:text-lg font-bold transition-all duration-200"
            style={{ color: '#ffffff' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#d0d0d0'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#ffffff'; }}
          >
            Privacy Policy
          </Link>
          <span className="hidden md:inline" style={{ color: '#ffffff', fontSize: '1rem', fontWeight: 'bold' }}>•</span>
          <a
            href="https://limewire.com/d/fURO2#ckR7K6yuJP"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex text-base md:text-lg font-bold transition-all duration-200"
            style={{ color: '#ffffff' }}
            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#d0d0d0'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#ffffff'; }}
          >
            Mobile App Download <Smartphone size={18} className="inline-block align-middle" />
          </a>
        </div>
      </div>
    </div>
    <div className="container pb-2 md:pb-4 flex flex-col items-center gap-1">
      <div className="flex items-center gap-3">
        <span style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.6rem' }}>&bull;</span>
        <Link
          to="/admin"
          className="text-xs opacity-30 hover:opacity-60 transition-opacity duration-200 text-white hidden md:inline-block"
        >
          Admin
        </Link>
      </div>
    </div>
  </footer>
  );
};

// ─── Character Carousel ───────────────────────────────────────────────────────

const CAROUSEL_IMAGES = [
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69cc01b31be1506975c95075',
    alt: 'Punk Dan — mohawk, piercings, spiked jacket',
    rotate: '-3deg',
    scale: 1,
  },
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69cc01bfa11d1e0fed5722df',
    alt: 'Warrior Dan — armor, red cape, sword',
    rotate: '2deg',
    scale: 1,
  },
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69cc01c8a11d1e0fed5722e0',
    alt: 'Soldier Dan — military helmet, camo, smirking',
    rotate: '-1.5deg',
    scale: 1,
  },
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69cc01cc1be1506975c95076',
    alt: 'Hoodie Dan — black hoodie, smirk — the original Dan',
    rotate: '1deg',
    scale: 1.06,
  },
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69cc01d31be1506975c95077',
    alt: 'Cowboy Dan — black hat, red bandana, beard',
    rotate: '-2.5deg',
    scale: 1,
  },
];

const CharacterCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection('right');
      setCurrentIndex(prev => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [currentIndex]);

  const img = CAROUSEL_IMAGES[currentIndex];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      {/* Carousel row: image */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', justifyContent: 'center' }}>
        {/* Image wrapper — clipping container */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            width: 'clamp(220px, 60vw, 280px)',
            flexShrink: 0,
          }}
        >
          {/* Tape strip */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-8px',
              left: '50%',
              marginLeft: '-30px',
              width: '60px',
              height: '20px',
              background: 'rgba(255,255,255,0.28)',
              borderRadius: '2px',
              transform: 'rotate(-2.5deg)',
              zIndex: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
              backdropFilter: 'blur(2px)',
            }}
          />
          {/* Rotation wrapper — holds sticker tilt, no animation here */}
          <div
            style={{
              transform: `rotate(${img.rotate}) scale(${img.scale})`,
              transformOrigin: 'center center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Image — animation only, no rotate in keyframes */}
            <img
              key={currentIndex}
              src={img.src}
              alt={img.alt}
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                border: '4px solid rgba(255,255,255,0.92)',
                borderRadius: '3px',
                boxShadow: '4px 6px 12px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.15)',
                animation: `carousel-slide-${direction} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) both`,
              }}
            />
          </div>
        </div>

      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {CAROUSEL_IMAGES.map((_, i) => (
          <div
            key={i}
            aria-label={`Character ${i + 1}`}
            style={{
              width: i === currentIndex ? '20px' : '8px',
              height: '8px',
              borderRadius: '4px',
              background: i === currentIndex ? '#7CFC00' : 'rgba(124,252,0,0.25)',
              border: 'none',
              padding: 0,
              transition: 'all 0.3s ease',
              boxShadow: i === currentIndex ? '0 0 8px rgba(124,252,0,0.6)' : 'none',
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── DanBettyCarousel ────────────────────────────────────────────────────────

const DAN_BETTY_IMAGES = [
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69ce1c56405f1549fd76cd3e',
    alt: 'Dan & Betty — alien pink couple',
  },
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69ce1c5a1506666752c11149',
    alt: 'Dan & Betty — older punk couple',
  },
  {
    src: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69ce1c5c405f1549fd76cd3f',
    alt: 'Dan & Betty — the originals in a cafe',
  },
];

const DanBettyCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGlitching, setIsGlitching] = useState(false);
  const [nextIndex, setNextIndex] = useState(1);

  useEffect(() => {
    const timer = setInterval(() => {
      const next = (currentIndex + 1) % DAN_BETTY_IMAGES.length;
      setNextIndex(next);
      setIsGlitching(true);
      setTimeout(() => {
        setCurrentIndex(next);
        setIsGlitching(false);
      }, 500);
    }, 3000);
    return () => clearInterval(timer);
  }, [currentIndex]);

  const current = DAN_BETTY_IMAGES[currentIndex];
  const upcoming = DAN_BETTY_IMAGES[nextIndex];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        margin: '4px 0',
      }}
    >
      {/* Image frame */}
      <div
        style={{
          position: 'relative',
          width: 'clamp(180px, 40%, 260px)',
          aspectRatio: '1 / 1',
          overflow: 'hidden',
          border: '2px solid rgba(124,252,0,0.3)',
          boxShadow: '0 0 18px rgba(124,252,0,0.12), 3px 3px 0 rgba(0,0,0,0.8)',
          flexShrink: 0,
        }}
      >
        {/* Current image — glitch-out when transitioning */}
        <img
          key={`current-${currentIndex}`}
          src={current.src}
          alt={current.alt}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            animation: isGlitching ? 'dan-betty-glitch-out 0.5s ease-out forwards' : undefined,
          }}
        />
        {/* Incoming image — glitch-in during transition */}
        {isGlitching && (
          <img
            key={`next-${nextIndex}`}
            src={upcoming.src}
            alt={upcoming.alt}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              animation: 'dan-betty-glitch-in 0.5s ease-out forwards',
            }}
          />
        )}
        {/* Scan line overlay */}
        {isGlitching && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              animation: 'dan-betty-scanline 0.5s ease-out forwards',
              background: 'linear-gradient(180deg, transparent 0%, rgba(124,252,0,0.18) 50%, transparent 100%)',
              backgroundSize: '100% 6px',
              zIndex: 3,
            }}
          />
        )}
        {/* RGB split ghost — red channel */}
        {isGlitching && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${upcoming.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'screen',
              opacity: 0,
              filter: 'saturate(0) sepia(1) hue-rotate(-20deg) brightness(1.6)',
              animation: 'dan-betty-rgb-red 0.5s ease-out forwards',
              zIndex: 2,
            }}
          />
        )}
        {/* RGB split ghost — cyan channel */}
        {isGlitching && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${upcoming.src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'screen',
              opacity: 0,
              filter: 'saturate(0) sepia(1) hue-rotate(160deg) brightness(1.6)',
              animation: 'dan-betty-rgb-cyan 0.5s ease-out forwards',
              zIndex: 2,
            }}
          />
        )}
      </div>

      {/* Caption */}
      <div
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '0.72rem',
          letterSpacing: '0.12em',
          color: 'rgba(124,252,0,0.45)',
          textTransform: 'uppercase',
          userSelect: 'none',
        }}
      >
        Dan &amp; Betty&hellip; across the multiverse
      </div>
    </div>
  );
};

// ─── LorePopup ────────────────────────────────────────────────────────────────

const LorePopup: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger scale-in animation on mount
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      playPop();
      onClose();
    }, 220);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        animation: 'lore-overlay-in 0.25s ease forwards',
      }}
      onClick={handleClose}
    >
      {/* Card */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          background: '#0a0a0a',
          border: '5px solid #E63946',
          boxShadow: '10px 10px 0 rgba(0,0,0,0.9), 0 0 60px rgba(230,57,70,0.35), inset 0 0 80px rgba(230,57,70,0.04)',
          transform: visible ? 'rotate(-1.5deg) scale(1)' : 'rotate(-1.5deg) scale(0.88)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.28s cubic-bezier(0.175,0.885,0.32,1.275), opacity 0.22s ease',
          borderRadius: '2px',
        }}
      >
        {/* Red corner accent — top left */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '60px',
            height: '60px',
            background: 'linear-gradient(135deg, rgba(230,57,70,0.18) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        {/* Red corner accent — bottom right */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '80px',
            height: '80px',
            background: 'linear-gradient(315deg, rgba(230,57,70,0.12) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />

        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close lore"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: '#E63946',
            border: '3px solid rgba(0,0,0,0.7)',
            boxShadow: '3px 3px 0 rgba(0,0,0,0.8)',
            color: '#fff',
            fontFamily: "'Bangers', cursive",
            fontSize: '1.1rem',
            letterSpacing: '0.08em',
            padding: '2px 10px 2px 10px',
            cursor: 'pointer',
            lineHeight: 1.4,
            transition: 'transform 0.1s ease, box-shadow 0.1s ease',
            zIndex: 10,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '4px 4px 0 rgba(0,0,0,0.9)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '3px 3px 0 rgba(0,0,0,0.8)';
          }}
        >
          ✕ CLOSE
        </button>

        {/* Scrollable content wrapper — overflowY lives here, NOT on the rotated card */}
        <div style={{ overflowY: 'auto', maxHeight: '90vh', padding: 'clamp(20px, 5vw, 40px)' }}>

        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: '28px', paddingTop: '4px' }}>
          <div
            className="lore-line"
            style={{ animationDelay: '0s' }}
          >
            <div
              style={{
                display: 'inline-block',
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(3rem, 10vw, 5rem)',
                letterSpacing: '0.1em',
                color: '#7CFC00',
                lineHeight: 0.95,
                textShadow: '0 0 30px rgba(124,252,0,0.5), 0 0 60px rgba(124,252,0,0.2), 4px 4px 0 rgba(0,0,0,0.8)',
              }}
            >
              DANGER DAN
            </div>
          </div>
          {/* Divider */}
          <div
            aria-hidden="true"
            className="lore-line"
            style={{
              height: '3px',
              background: 'linear-gradient(90deg, transparent 0%, #E63946 30%, #E63946 70%, transparent 100%)',
              margin: '10px auto 0',
              maxWidth: '260px',
              animationDelay: '0.3s',
            }}
          />
        </div>

        {/* Lore body */}
        <div
          style={{
            fontFamily: "'Georgia', 'Times New Roman', serif",
            color: 'rgba(255,255,255,0.88)',
            lineHeight: 1.85,
            letterSpacing: '0.025em',
            fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)',
          }}
        >
          <div className="lore-line" style={{ animationDelay: '0.5s', marginBottom: '18px' }}>
            <p style={{ margin: 0 }}>
              In every universe, there is a Dan… and there is a Betty.
            </p>
          </div>

          {/* Dan & Betty multiverse carousel */}
          <div className="lore-line" style={{ animationDelay: '0.7s', marginBottom: '18px' }}>
            <DanBettyCarousel />
          </div>

          <div className="lore-line" style={{ animationDelay: '0.9s', marginBottom: '18px' }}>
            <p style={{ margin: 0 }}>
              The one constant across infinite realities.
            </p>
          </div>

          <div className="lore-line" style={{ animationDelay: '1.4s', marginBottom: '18px' }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(1.4rem, 4vw, 1.9rem)',
                letterSpacing: '0.12em',
                color: '#E63946',
                textShadow: '0 0 16px rgba(230,57,70,0.5)',
              }}
            >
              Until now.
            </p>
          </div>

          <div className="lore-line" style={{ animationDelay: '2.0s', marginBottom: '18px' }}>
            <p style={{ margin: 0 }}>
              Across the multiverse, Betties are either dead or disappearing — one by one — until only a single Dan remains with his.
            </p>
          </div>

          {/* Comic-style inline image after "remains with his" paragraph */}
          <div className="lore-line" style={{ animationDelay: '1.8s', marginBottom: '18px' }}>
            {/* Comic tag */}
            <div
              aria-hidden="true"
              style={{
                display: 'block',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  background: '#E63946',
                  color: '#fff',
                  fontFamily: "'Bangers', cursive",
                  fontSize: '0.75rem',
                  letterSpacing: '0.14em',
                  padding: '2px 10px',
                  border: '2px solid rgba(0,0,0,0.8)',
                  boxShadow: '2px 2px 0 rgba(0,0,0,0.7)',
                  transform: 'rotate(-1.2deg)',
                  userSelect: 'none',
                  lineHeight: 1.3,
                }}
              >
                EARTH 161
              </div>
            </div>
            {/* Image */}
            <img
              src="https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69ce1cda1506666752c1114a"
              alt="Earth 161 — A Dan and Betty moment across the multiverse"
              style={{
                display: 'block',
                width: '100%',
                maxWidth: '320px',
                margin: '0 auto',
                border: '4px solid rgba(255,255,255,0.9)',
                boxShadow: '5px 5px 0 rgba(0,0,0,0.7)',
                transform: 'rotate(1.5deg)',
                borderRadius: '2px',
              }}
            />
          </div>

          <div className="lore-line" style={{ animationDelay: '2.6s', marginBottom: '18px' }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(1.3rem, 3.5vw, 1.75rem)',
                letterSpacing: '0.1em',
                color: '#E63946',
                textShadow: '2px 2px 0 rgba(0,0,0,0.9), 0 0 20px rgba(230,57,70,0.45)',
                borderLeft: '4px solid #E63946',
                paddingLeft: '14px',
                lineHeight: 1.25,
              }}
            >
              Dan is in danger…<br />but the danger is also Dan.
            </p>
          </div>

          <div className="lore-line" style={{ animationDelay: '3.2s', marginBottom: '18px' }}>
            <p style={{ margin: 0 }}>
              Worlds collide. Variants awaken. Some seek the truth. Others want someone to blame.
            </p>
          </div>

          <div className="lore-line" style={{ animationDelay: '3.8s', marginBottom: '18px' }}>
            <p
              style={{
                margin: 0,
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(1.4rem, 4vw, 1.9rem)',
                letterSpacing: '0.12em',
                color: '#fff',
                textShadow: '0 0 12px rgba(255,255,255,0.2)',
              }}
            >
              And now —
            </p>
          </div>

          <div className="lore-line" style={{ animationDelay: '4.3s', marginBottom: '28px' }}>
            <p style={{ margin: 0 }}>
              Every eye, every reality, every Dan… is coming for the last one who still has a Betty.
            </p>
          </div>

          {/* Final call-to-action line */}
          <div
            className="lore-line"
            style={{ animationDelay: '5.0s' }}
          >
            <div
              style={{
                textAlign: 'center',
                fontFamily: "'Bangers', cursive",
                fontSize: 'clamp(1.6rem, 5vw, 2.4rem)',
                letterSpacing: '0.13em',
                color: '#7CFC00',
                textShadow: '0 0 24px rgba(124,252,0,0.6), 0 0 50px rgba(124,252,0,0.2), 3px 3px 0 rgba(0,0,0,0.9)',
                lineHeight: 1.2,
                padding: '16px 0 4px',
                borderTop: '2px dashed rgba(124,252,0,0.2)',
              }}
            >
              Will you protect him…<br />or hunt him?
            </div>
          </div>

          {/* Character carousel — slapped onto the bottom of the card */}
          <div
            className="lore-line"
            style={{
              animationDelay: '5.5s',
              marginTop: '28px',
              marginBottom: '4px',
              pointerEvents: 'none',
            }}
          >
            <CharacterCarousel />
          </div>
        </div>

        </div>{/* end scrollable content wrapper */}
      </div>

      <style>{`
        @keyframes lore-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes lore-reveal {
          0%   { opacity: 0; transform: translateY(12px); filter: blur(2px); }
          100% { opacity: 1; transform: translateY(0);    filter: blur(0);   }
        }
        .lore-line {
          opacity: 0;
          animation: lore-reveal 0.6s ease-out forwards;
        }
        @keyframes carousel-slide-right {
          from { opacity: 0; transform: translateX(60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes carousel-slide-left {
          from { opacity: 0; transform: translateX(-60px); }
          to   { opacity: 1; transform: translateX(0); }
        }

        /* ── Dan & Betty glitch transition ────────────────────────────── */

        /* Outgoing image: flicker and distort off */
        @keyframes dan-betty-glitch-out {
          0%   { opacity: 1; transform: translateX(0)       skewX(0deg);     filter: brightness(1)   hue-rotate(0deg); }
          10%  { opacity: 1; transform: translateX(-4px)    skewX(-1deg);    filter: brightness(1.6) hue-rotate(90deg); }
          18%  { opacity: 0; transform: translateX(6px)     skewX(2deg);     filter: brightness(2)   hue-rotate(180deg); }
          22%  { opacity: 1; transform: translateX(-8px)    skewX(-3deg);    filter: brightness(0.4) hue-rotate(270deg); }
          30%  { opacity: 0; transform: translateX(3px)     skewX(1.5deg);   filter: brightness(0)   hue-rotate(0deg); }
          100% { opacity: 0; transform: translateX(0);                        filter: brightness(0); }
        }

        /* Incoming image: jitter in from digital noise */
        @keyframes dan-betty-glitch-in {
          0%   { opacity: 0; transform: translateX(10px)    skewX(3deg);     filter: brightness(3)   hue-rotate(90deg)  blur(2px); clip-path: inset(40% 0 40% 0); }
          8%   { opacity: 1; transform: translateX(-7px)    skewX(-2deg);    filter: brightness(2)   hue-rotate(180deg) blur(1px); clip-path: inset(20% 0 60% 0); }
          15%  { opacity: 0; transform: translateX(5px)     skewX(1.5deg);   filter: brightness(4)   hue-rotate(270deg) blur(0px); clip-path: inset(0% 0 80% 0); }
          22%  { opacity: 1; transform: translateX(-3px)    skewX(-1deg);    filter: brightness(1.8) hue-rotate(45deg);             clip-path: inset(0% 0 0% 0); }
          30%  { opacity: 0; transform: translateX(8px)     skewX(2.5deg);   filter: brightness(2.5) hue-rotate(0deg); }
          38%  { opacity: 1; transform: translateX(-5px)    skewX(-1.5deg);  filter: brightness(1.4) hue-rotate(30deg); }
          50%  { opacity: 1; transform: translateX(2px)     skewX(0.5deg);   filter: brightness(1.2) hue-rotate(10deg); }
          65%  { opacity: 1; transform: translateX(-1px)    skewX(0deg);     filter: brightness(1.05) hue-rotate(0deg); }
          80%  { opacity: 1; transform: translateX(0.5px);                   filter: brightness(1); }
          100% { opacity: 1; transform: translateX(0)       skewX(0deg);     filter: brightness(1)   hue-rotate(0deg)  blur(0px); }
        }

        /* Scan line sweep */
        @keyframes dan-betty-scanline {
          0%   { opacity: 0; transform: translateY(-100%); }
          15%  { opacity: 1; transform: translateY(-50%); }
          40%  { opacity: 0.6; transform: translateY(0%); }
          70%  { opacity: 0.3; transform: translateY(60%); }
          100% { opacity: 0; transform: translateY(120%); }
        }

        /* RGB red ghost — offset left */
        @keyframes dan-betty-rgb-red {
          0%   { opacity: 0;    transform: translateX(0); }
          10%  { opacity: 0.55; transform: translateX(-8px); }
          25%  { opacity: 0.35; transform: translateX(-5px); }
          40%  { opacity: 0.2;  transform: translateX(-2px); }
          60%  { opacity: 0;    transform: translateX(0); }
          100% { opacity: 0; }
        }

        /* RGB cyan ghost — offset right */
        @keyframes dan-betty-rgb-cyan {
          0%   { opacity: 0;    transform: translateX(0); }
          10%  { opacity: 0.55; transform: translateX(8px); }
          25%  { opacity: 0.35; transform: translateX(5px); }
          40%  { opacity: 0.2;  transform: translateX(2px); }
          60%  { opacity: 0;    transform: translateX(0); }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// ─── HomePage ─────────────────────────────────────────────────────────────────

export const HomePage: React.FC = () => {
  const teamButtonRef = useScrollAnimation<HTMLElement>(0.05);
  const artistButtonRef = useScrollAnimation<HTMLElement>(0.05);
  const loreButtonRef = useScrollAnimation<HTMLElement>(0.05);
  const dangerRoomButtonRef = useScrollAnimation<HTMLElement>(0.05);
  const [showLore, setShowLore] = useState(false);

  // Lock body scroll when popup is open
  useEffect(() => {
    if (showLore) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showLore]);

  return (
    <>
    <WalletWarningBanner />
    <PageLayout fullBleed footer={<DangerFooter />}>
      {/* Lore popup */}
      {showLore && <LorePopup onClose={() => setShowLore(false)} />}

      {/* TV grain / static noise overlay — fixed, behind all content */}
      <div className="tv-grain" aria-hidden="true" />

      <DangerHero />
      <VideoTeaser />

      {/* Lore button */}
      <section ref={loreButtonRef} className="relative py-12 flex flex-col items-center justify-center scroll-fade-scale">
        {/* Mobile dark backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{ background: 'rgba(8,5,5,0.88)', zIndex: 0 }}
        />
        {/* Subtle red halftone dots */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(230,57,70,1) 1px, transparent 1px)',
            backgroundSize: '26px 26px',
            opacity: 0.022,
          }}
        />
        <div className="relative z-10 flex flex-col items-center gap-4">
          {/* Comic tag above */}
          <div
            aria-hidden="true"
            style={{
              display: 'inline-block',
              background: '#E63946',
              color: '#fff',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.14em',
              padding: '2px 12px',
              border: '3px solid rgba(0,0,0,0.75)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.6)',
              transform: 'rotate(1.2deg)',
              userSelect: 'none',
              lineHeight: 1.4,
            }}
          >
            THE MULTIVERSE AWAITS
          </div>

          <button
            onClick={() => { playWhoosh(); setShowLore(true); }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLButtonElement;
              playHover();
              el.style.transform = 'rotate(-1.8deg) scale(1.07) translateY(-4px)';
              el.style.boxShadow = '8px 8px 0 rgba(0,0,0,0.9), 0 0 40px rgba(230,57,70,0.55)';
              el.style.color = '#E63946';
              el.style.borderColor = '#E63946';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.transform = 'rotate(-1.8deg) scale(1) translateY(0)';
              el.style.boxShadow = '6px 6px 0 rgba(0,0,0,0.85), 0 0 20px rgba(230,57,70,0.2)';
              el.style.color = '#fff';
              el.style.borderColor = '#E63946';
            }}
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              letterSpacing: '0.12em',
              color: '#fff',
              background: 'linear-gradient(135deg, #0d0607 0%, #160a0a 50%, #0a0a14 100%)',
              border: '4px solid #E63946',
              boxShadow: '6px 6px 0 rgba(0,0,0,0.85), 0 0 20px rgba(230,57,70,0.2)',
              padding: '10px 36px',
              transform: 'rotate(-1.8deg)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease, color 0.18s ease',
            }}
          >
            {/* Book/scroll icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            </svg>
            DISCOVER THE LORE
          </button>
        </div>
      </section>

      {/* Artist Room button */}
      <section ref={artistButtonRef} className="relative py-8 flex flex-col items-center justify-center scroll-fade-scale">
        {/* Mobile dark backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{ background: 'rgba(8,13,31,0.82)', zIndex: 0 }}
        />
        <div className="relative z-10 flex flex-col items-center gap-4">
          {/* Small comic label above */}
          <div
            aria-hidden="true"
            style={{
              display: 'inline-block',
              background: '#7CFC00',
              color: '#0a0a0f',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.14em',
              padding: '2px 12px',
              border: '3px solid rgba(0,0,0,0.75)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.5)',
              transform: 'rotate(-1.4deg)',
              userSelect: 'none',
              lineHeight: 1.4,
            }}
          >
            BEHIND THE SCENES
          </div>

          <Link
            to="/artist"
            onClick={playWhoosh}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              playHover();
              el.style.transform = 'rotate(1.5deg) scale(1.07) translateY(-4px)';
              el.style.boxShadow = '8px 8px 0 rgba(0,0,0,0.8), 0 0 30px rgba(124,252,0,0.45)';
              el.style.color = '#7CFC00';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.transform = 'rotate(1.5deg) scale(1) translateY(0)';
              el.style.boxShadow = '6px 6px 0 rgba(0,0,0,0.8)';
              el.style.color = '#fff';
            }}
            className="inline-flex items-center gap-3 transition-all duration-200"
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              letterSpacing: '0.12em',
              color: '#fff',
              background: 'linear-gradient(135deg, #0a0a14 0%, #111120 100%)',
              border: '4px solid #7CFC00',
              boxShadow: '6px 6px 0 rgba(0,0,0,0.8)',
              padding: '10px 36px',
              transform: 'rotate(1.5deg)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* Palette icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" stroke="none" />
              <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" stroke="none" />
              <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" stroke="none" />
              <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" stroke="none" />
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
            </svg>
            ARTIST ROOM
          </Link>
        </div>
      </section>

      {/* Danger Room button */}
      <section ref={dangerRoomButtonRef} className="relative py-8 flex flex-col items-center justify-center scroll-fade-scale">
        {/* Mobile dark backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{ background: 'rgba(8,13,31,0.82)', zIndex: 0 }}
        />
        {/* Hazard stripe decoration */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(230,57,70,0.04) 0px, rgba(230,57,70,0.04) 10px, transparent 10px, transparent 20px)`,
            zIndex: 0,
          }}
        />
        <div className="relative z-10 flex flex-col items-center gap-4">
          {/* Small comic label above */}
          <div
            aria-hidden="true"
            style={{
              display: 'inline-block',
              background: '#E63946',
              color: '#fff',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.14em',
              padding: '2px 12px',
              border: '3px solid rgba(0,0,0,0.75)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.5)',
              transform: 'rotate(1.2deg)',
              userSelect: 'none',
              lineHeight: 1.4,
            }}
          >
            ⚠ ENTER THE ZONE
          </div>

          <Link
            to="/danger-room"
            onClick={playWhoosh}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              playHover();
              el.style.transform = 'rotate(-1.8deg) scale(1.07) translateY(-4px)';
              el.style.boxShadow = '8px 8px 0 rgba(0,0,0,0.8), 0 0 30px rgba(230,57,70,0.55)';
              el.style.borderColor = '#FF6B35';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.transform = 'rotate(-1.8deg) scale(1) translateY(0)';
              el.style.boxShadow = '6px 6px 0 rgba(0,0,0,0.8)';
              el.style.borderColor = '#E63946';
            }}
            className="inline-flex items-center gap-3 transition-all duration-200"
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              letterSpacing: '0.12em',
              color: '#fff',
              background: 'linear-gradient(135deg, #1a0505 0%, #2a0808 100%)',
              border: '4px solid #E63946',
              boxShadow: '6px 6px 0 rgba(0,0,0,0.8)',
              padding: '10px 36px',
              transform: 'rotate(-1.8deg)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* Skull / hazard icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 22h6" />
              <path d="M10 17v-2" />
              <path d="M14 17v-2" />
              <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.26C17.81 13.47 19 11.38 19 9a7 7 0 0 0-7-7z" />
            </svg>
            DANGER ROOM
          </Link>
        </div>
      </section>

      {/* Meet the Team button */}
      <section ref={teamButtonRef} className="relative py-16 flex flex-col items-center justify-center scroll-fade-scale">
        {/* Mobile dark backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{ background: 'rgba(8,13,31,0.82)', zIndex: 0 }}
        />
        {/* Subtle halftone dot bg */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(124,252,0,1) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
            opacity: 0.025,
          }}
        />
        <div className="relative z-10 flex flex-col items-center gap-4">
          {/* Small comic label above */}
          <div
            aria-hidden="true"
            style={{
              display: 'inline-block',
              background: '#E63946',
              color: '#fff',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.14em',
              padding: '2px 12px',
              border: '3px solid rgba(0,0,0,0.75)',
              boxShadow: '3px 3px 0 rgba(0,0,0,0.5)',
              transform: 'rotate(1.8deg)',
              userSelect: 'none',
              lineHeight: 1.4,
            }}
          >
            WHO MADE THIS?!
          </div>

          <Link
            to="/team"
            onClick={playWhoosh}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              playHover();
              el.style.transform = 'rotate(-2deg) scale(1.07) translateY(-4px)';
              el.style.boxShadow = '8px 8px 0 rgba(0,0,0,0.8), 0 0 30px rgba(124,252,0,0.55)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.transform = 'rotate(-2deg) scale(1) translateY(0)';
              el.style.boxShadow = '6px 6px 0 rgba(0,0,0,0.8)';
            }}
            className="inline-flex items-center gap-3 transition-all duration-200"
            style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
              letterSpacing: '0.12em',
              color: '#0a0a0f',
              background: '#7CFC00',
              border: '4px solid rgba(0,0,0,0.85)',
              boxShadow: '6px 6px 0 rgba(0,0,0,0.8)',
              padding: '10px 36px',
              transform: 'rotate(-2deg)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* Arrow icon */}
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
            MEET THE TEAM
          </Link>
        </div>
      </section>

      {/* Lucky Draw section */}
      <LuckyDraw />

    </PageLayout>
    </>
  );
};

export default HomePage;
